import {alpha, CardActions, CardContent, CardMedia, Divider, Grid} from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card/Card";
import {IWish} from "../../../../store/wish/Helper";
import * as React from "react";
import {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {RoutePaths} from "../../../../util/constants";
import Avatar from "@mui/material/Avatar";
import ReactGA from "react-ga4";
import CompanyLabel from "../../../shared/company-label/CompanyLabel";
import {useTheme} from "@mui/material/styles";
import Confetti from "react-confetti";
import {RootState, useAppSelector} from "../../../../store";
import {Link as RouterLink} from "react-router-dom";
import GiftBow from "../../../shared/gift-bow/GiftBow";

interface IProps {
    wish: IWish
}

const PastWishesItem: FC<IProps> = (props) => {
    const {t, i18n} = useTranslation()
    const theme = useTheme()
    const {email} = useAppSelector((state: RootState) => state.authReducer.loggedInUser)

    const handleSeeWishDetailsButton = (wishId: string) => {
        ReactGA.event("select_item", {items: [{item_id: wishId, item_name: 'Wish - Past Winners'}]})
    }

    const isJpgThumbnail = props.wish.thumbnail.toLowerCase().endsWith('.jpg') || props.wish.thumbnail.toLowerCase().endsWith('.jpeg')

    const isWinner = useMemo(() => {
        if (props.wish.winner?.ticket.recipient_email) {
            return props.wish.winner?.ticket.recipient_email === email
        }
        return props.wish.winner?.ticket.user.email === email
    }, [props.wish.winner?.ticket.recipient_email, email, props.wish.winner?.ticket.user.email])

    return (
        <Card elevation={3} sx={{
            display: 'flex',
            flexDirection: 'column',
            width: {xs: '100%', sm: 265},
            backgroundColor: "transparent",
            border: `1px solid ${theme.palette.tertiary.main}`,
            padding: 1,
            my: 2,
            mx: 0.8,
            position: 'relative'
        }}>
            {isWinner && <Confetti
                gravity={0.05}
            />}
            <GiftBow showRibbon={true}/>
            <Typography variant="h5" fontWeight="bold" sx={{color: theme.palette.tertiary.main}} textAlign="center">
                {t('winner')}:
            </Typography>
            <Grid container flexWrap="nowrap" justifyContent="center" alignItems="center">
                <Avatar sx={{width: 42, height: 42, mr: 1}}
                        src={props.wish.winner?.ticket.recipient_name ? '' : props.wish.winner?.ticket?.user.profile_picture}
                        alt={props.wish.winner?.ticket.recipient_name ?? props.wish.winner?.ticket.user.full_name!}/>
                <Typography variant="subtitle1" fontWeight="bold" sx={{color: theme.palette.tertiary.main}}>
                    {props.wish.winner?.ticket.recipient_name ?? props.wish.winner?.ticket.user.full_name ?? ''}
                </Typography>
            </Grid>

            <Divider sx={{
                backgroundColor: alpha(theme.palette.tertiary.main, .6),
                my: 2
            }}/>

            <Typography variant="h5" fontWeight="bold" sx={{color: theme.palette.tertiary.main}} textAlign="center"
                        textTransform="capitalize"
                        mb={1}>
                {t('wish')}:
            </Typography>
            <CardMedia
                component="img"
                sx={{
                    objectFit: isJpgThumbnail ? 'cover' : 'contain',
                    height: 'auto',
                    width: '100%',
                    aspectRatio: '16/9'
                }}
                image={props.wish.thumbnail}
                alt="Wish thumbnail"
            />
            <CardContent sx={{flex: 1}}>
                <Typography textAlign='center' variant="h5" sx={{color: theme.palette.tertiary.main}} fontWeight={600}>
                    {props.wish[`name_${i18n.language}`]}
                </Typography>
                <Grid container justifyContent="center">
                    <CompanyLabel company={props.wish.company} companyUrl={props.wish.company_url}
                                  color={theme.palette.tertiary.main}/>
                </Grid>
            </CardContent>
            <CardActions sx={{justifyContent: "center"}}>
                <Button variant="outlined"
                        component={RouterLink}
                        to={`${RoutePaths.WISHES}/${props.wish.id}`}
                        color="tertiary"
                        onClick={() => handleSeeWishDetailsButton(props.wish.id)}>{t('see_wish_details')}</Button>
            </CardActions>
        </Card>
    )
}

export default PastWishesItem
